import {
  type LandingHeroCopy,
  type LandingHeroImageAdmin,
  landingHeroCopyFieldsSchema,
} from '@repo/api-contracts';

/** 배너 문구 폼의 입력 칸 — 순서가 화면 순서다. */
export const LANDING_HERO_COPY_FIELDS = [
  'eyebrow',
  'title',
  'subtitle',
  'ctaLabel',
  'ctaHref',
] as const satisfies readonly (keyof LandingHeroCopy)[];
export type LandingHeroCopyFieldName = (typeof LANDING_HERO_COPY_FIELDS)[number];

/** 폼 값 — 입력 칸은 빈 문자열을 "비움"으로 쓴다(저장할 때 null 로 바뀐다). */
export type LandingHeroCopyFormValues = Record<LandingHeroCopyFieldName, string>;
export type LandingHeroCopyFormErrors = Partial<Record<LandingHeroCopyFieldName, string>>;

export const LANDING_HERO_COPY_LABELS: Record<LandingHeroCopyFieldName, string> = {
  eyebrow: '작은 라벨',
  title: '큰 문구',
  subtitle: '보조 문구',
  ctaLabel: '버튼 문구',
  ctaHref: '버튼 링크',
};

export const toLandingHeroCopyFormValues = (copy: LandingHeroCopy): LandingHeroCopyFormValues => ({
  eyebrow: copy.eyebrow ?? '',
  title: copy.title ?? '',
  subtitle: copy.subtitle ?? '',
  ctaLabel: copy.ctaLabel ?? '',
  ctaHref: copy.ctaHref ?? '',
});

/** 저장 직전 검사 — 서버와 같은 계약 스키마. 통과하면 공백이 정리된 저장 값을 돌려준다. */
export const parseLandingHeroCopyForm = (
  values: LandingHeroCopyFormValues,
):
  | { readonly ok: true; readonly copy: LandingHeroCopy }
  | { readonly ok: false; readonly errors: LandingHeroCopyFormErrors } => {
  const result = landingHeroCopyFieldsSchema.safeParse(values);

  if (result.success) {
    return { ok: true, copy: result.data };
  }

  const errors: LandingHeroCopyFormErrors = {};
  for (const issue of result.error.issues) {
    const field = LANDING_HERO_COPY_FIELDS.find((name) => name === issue.path[0]);
    if (field !== undefined && errors[field] === undefined) {
      errors[field] = issue.message;
    }
  }
  return { ok: false, errors };
};

/**
 * 칸마다 비웠을 때 HOME 에 나올 값 안내. 공개 상품이 연결돼 있으면 그 상품 정보로 대신하고,
 * 아니면 그 칸은 나오지 않는다(web home-hero 의 우선순위와 같다).
 */
export const buildLandingHeroCopyPlaceholders = (
  product: LandingHeroImageAdmin['product'],
): Record<LandingHeroCopyFieldName, string> => {
  const isPublic = product !== null && product.status === 'PUBLISHED' && !product.isDeleted;

  if (!isPublic) {
    return {
      eyebrow: '예: 추천 상품 — 비우면 표시 안 함',
      title: '예: 우리 팀의 첫 굿즈를 디자인하세요 — 비우면 표시 안 함',
      subtitle: '예: 티셔츠·머그·텀블러를 직접 디자인하고 바로 견적까지',
      ctaLabel: '예: 스토어 보기 — 비우면 버튼 없음',
      ctaHref: '예: /store 또는 https://…',
    };
  }

  return {
    eyebrow: '비우면 연결 상품: 추천 상품',
    title: `비우면 연결 상품: ${product.name}`,
    subtitle: '비우면 연결 상품: 최소 N개 · 개당 N원부터',
    ctaLabel: '비우면 연결 상품: 보기',
    ctaHref: '비우면 연결 상품 페이지',
  };
};
